import { useEffect, useState } from "react";
import { Users2, TrendingDown, TrendingUp, AlertTriangle, Plus, Clipboard, Trash2, Award, Activity, X, ListOrdered, ChevronRight, Building2, Timer, UserMinus, Mail, Receipt } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const TeamDashboard = () => {
  const [data, setData] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [assignTarget, setAssignTarget] = useState({ user_id: "", scenario_id: "", note: "" });
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name, email }
  const [orgInfo, setOrgInfo] = useState(null); // { in_org, organization, is_owner }
  const [orgUsage, setOrgUsage] = useState(null);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [addMemberEmail, setAddMemberEmail] = useState("");
  const [orgBusy, setOrgBusy] = useState(false);

  const loadOrg = async () => {
    try {
      const me = await api.get("/org/me");
      setOrgInfo(me.data);
      if (me.data?.is_owner) {
        const u = await api.get("/org/usage");
        setOrgUsage(u.data);
      }
    } catch (e) { /* org is optional */ }
  };

  const createOrg = async () => {
    if (!newOrgName.trim()) return toast.error("Bitte Namen vergeben");
    setOrgBusy(true);
    try {
      await api.post("/org/create", { name: newOrgName.trim() });
      toast.success("Organisation angelegt");
      setShowCreateOrg(false); setNewOrgName("");
      await loadOrg();
    } catch (e) { toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen"); }
    finally { setOrgBusy(false); }
  };

  const addOrgMember = async () => {
    if (!addMemberEmail.trim()) return;
    setOrgBusy(true);
    try {
      await api.post("/org/members", { email: addMemberEmail.trim() });
      toast.success("Mitglied hinzugefügt");
      setAddMemberEmail("");
      await loadOrg();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hinzufügen fehlgeschlagen"); }
    finally { setOrgBusy(false); }
  };

  const removeOrgMember = async (uid, name) => {
    if (!window.confirm(`${name} aus der Organisation entfernen?`)) return;
    try {
      await api.delete(`/org/members/${uid}`);
      toast.success("Mitglied entfernt");
      await loadOrg();
    } catch (e) { toast.error(e?.response?.data?.detail || "Entfernen fehlgeschlagen"); }
  };

  const deleteUser = async () => {
    if (!confirmDelete) return;
    try {
      const { data: res } = await api.delete(`/admin/users/${confirmDelete.id}`);
      toast.success(`${confirmDelete.name} gelöscht`, {
        description: `${res.deleted.sessions} Sessions, ${res.deleted.messages} Nachrichten mitentfernt`,
      });
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const load = async () => {
    try {
      const [a, as, sc, eng] = await Promise.all([
        api.get("/admin/analytics/team"),
        api.get("/admin/assignments"),
        api.get("/scenarios"),
        api.get("/admin/analytics/engagement").catch(() => ({ data: null })),
      ]);
      setData(a.data); setAssignments(as.data || []); setScenarios(sc.data);
      setEngagement(eng.data);
    } catch (e) { toast.error("Konnte Team-Daten nicht laden"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); loadOrg(); }, []);

  const createAssignment = async () => {
    if (!assignTarget.user_id || !assignTarget.scenario_id) return toast.error("Verkäufer und Szenario wählen");
    try {
      await api.post("/admin/assignments", assignTarget);
      toast.success("Pflicht-Training zugewiesen");
      setShowAssign(false);
      setAssignTarget({ user_id: "", scenario_id: "", note: "" });
      load();
    } catch (e) { toast.error("Zuweisung fehlgeschlagen"); }
  };

  const deleteAssignment = async (id) => {
    if (!window.confirm("Zuweisung löschen?")) return;
    await api.delete(`/admin/assignments/${id}`);
    toast.success("Gelöscht"); load();
  };

  if (loading || !data) return <div className="p-12 text-center text-novatus-text-3">Lade Team-Analytics...</div>;

  const sortedUsers = [...(data.users || [])].sort((a, b) => b.average_score - a.average_score);
  const top3 = sortedUsers.slice(0, 3);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="animate-slide-up flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="label-eyebrow">Trainer · Team-Übersicht</span>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2">
            <span className="text-novatus-green">{data.total_team_size}</span> Verkäufer im Training.
          </h1>
          <p className="text-novatus-text-2 mt-3 max-w-2xl">Performance, Schwächen und gezielte Pflicht-Trainings auf einen Blick.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/team/billing" className="btn-ghost-green h-11 px-5 rounded-lg flex items-center gap-2" data-testid="billing-link">
            <Receipt className="w-4 h-4" /> Abrechnung
          </Link>
          <Link to="/team/sessions" className="btn-ghost-green h-11 px-5 rounded-lg flex items-center gap-2" data-testid="all-sessions-btn">
            <ListOrdered className="w-4 h-4" /> Alle Sessions
          </Link>
          <button onClick={() => setShowAssign(true)} className="btn-green h-11 px-5 rounded-lg flex items-center gap-2" data-testid="assign-btn">
            <Plus className="w-4 h-4" /> Pflicht-Training zuweisen
          </button>
        </div>
      </div>

      {/* Organization / Team-Pool panel */}
      {orgInfo && (
        <div className="mt-6 nv-card p-5" data-testid="org-panel">
          {!orgInfo.in_org ? (
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 rounded-full bg-novatus-green/10 border border-novatus-green/40 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-novatus-green" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-white">Team-Minuten-Pool starten</h3>
                  <p className="text-sm text-novatus-text-2 mt-1">
                    Lege eine Organisation an, kaufe ein Team- oder Enterprise-Abo, und Deine Verkäufer ziehen Minuten aus dem gemeinsamen Pool.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateOrg(true)}
                className="btn-green h-10 px-5 rounded-lg shrink-0"
                data-testid="create-org-btn"
              >
                Organisation anlegen
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-novatus-green/10 border border-novatus-green/40 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-novatus-green" />
                  </div>
                  <div>
                    <p className="label-eyebrow text-novatus-green">Aktive Organisation</p>
                    <h3 className="font-display font-bold text-white text-xl">{orgInfo.organization?.name}</h3>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs">
                      <span className="text-novatus-text-2">Pool-Guthaben: <span className="text-white font-mono font-bold">{(orgInfo.organization?.minutes_balance || 0).toFixed(1)} Min</span></span>
                      <span className="text-novatus-text-2">Verbraucht (lifetime): <span className="text-white font-mono">{(orgInfo.organization?.minutes_lifetime_used || 0).toFixed(1)} Min</span></span>
                      {orgInfo.organization?.plan_id && (
                        <span className="text-novatus-text-2">Abo: <span className="text-novatus-green font-bold uppercase">{orgInfo.organization.plan_id}</span></span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {orgInfo.is_owner && (
                <>
                  <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-novatus">
                    <input
                      type="email"
                      value={addMemberEmail}
                      onChange={(e) => setAddMemberEmail(e.target.value)}
                      placeholder="E-Mail des Verkäufers hinzufügen (muss registriert sein)"
                      className="flex-1 bg-novatus-card-2 border border-novatus rounded-lg px-3 py-2 text-sm text-white placeholder-novatus-text-3 focus:outline-none focus:border-novatus-green"
                      data-testid="add-org-member-input"
                    />
                    <button
                      onClick={addOrgMember}
                      disabled={orgBusy || !addMemberEmail}
                      className="btn-green h-10 px-4 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="add-org-member-btn"
                    >
                      <Mail className="w-4 h-4" /> Mitglied einladen
                    </button>
                  </div>

                  {orgUsage?.by_member && orgUsage.by_member.length > 0 && (
                    <div>
                      <p className="label-eyebrow mb-2">Verbrauch der letzten 30 Tage</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {orgUsage.by_member.map(m => (
                          <div key={m.user_id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-novatus-card-2 border border-novatus text-sm" data-testid={`org-usage-${m.user_id}`}>
                            <span className="text-white truncate flex items-center gap-2">
                              <Timer className="w-3.5 h-3.5 text-novatus-green" />
                              {m.name || "—"}
                            </span>
                            <span className="text-novatus-text-2 font-mono shrink-0">{m.minutes_used.toFixed(1)} Min · {m.sessions}×</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
        <div className="nv-card p-6 nv-card-hover animate-slide-up delay-1" data-testid="team-metric-size">
          <p className="label-eyebrow">Team-Größe</p>
          <p className="font-display font-black text-4xl text-white mt-2">{data.total_team_size}</p>
          <p className="text-xs text-novatus-text-3 mt-1">Aktive Verkäufer</p>
        </div>
        <div className="nv-card p-6 nv-card-hover animate-slide-up delay-2" data-testid="team-metric-sessions">
          <p className="label-eyebrow">Trainings</p>
          <p className="font-display font-black text-4xl text-white mt-2">{data.total_sessions}</p>
          <p className="text-xs text-novatus-text-3 mt-1">Insgesamt absolviert</p>
        </div>
        <div className="nv-card p-6 nv-card-hover animate-slide-up delay-2" data-testid="team-metric-avg">
          <p className="label-eyebrow">Ø Team-Score</p>
          <p className="font-display font-black text-4xl text-novatus-green mt-2">{data.team_average_score || 0}</p>
          <p className="text-xs text-novatus-text-3 mt-1">über alle Trainings</p>
        </div>
        <div className="nv-card p-6 nv-card-hover animate-slide-up delay-3" data-testid="team-metric-assignments">
          <p className="label-eyebrow">Offene Pflichten</p>
          <p className="font-display font-black text-4xl text-amber-400 mt-2">{assignments.filter(a => a.status === "open").length}</p>
          <p className="text-xs text-novatus-text-3 mt-1">Trainer-Zuweisungen</p>
        </div>
      </div>

      {/* Engagement metrics (Direktor / Admin) */}
      {engagement && (
        <div className="mt-5 nv-card p-6 animate-slide-up delay-3" data-testid="engagement-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-novatus-green" />
                <h3 className="font-display font-bold text-lg text-white">Engagement-Übersicht</h3>
              </div>
              <p className="text-xs text-novatus-text-3 mt-1">Onboarding-Fortschritt, Login-Aktivität und veraltete Accounts.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-5">
            {/* Onboarding completion */}
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-4" data-testid="engagement-onboarding">
              <p className="label-eyebrow">Onboarding</p>
              <p className="font-display font-black text-3xl text-white mt-1">
                {engagement.onboarding.completed}<span className="text-novatus-text-3 text-xl">/{engagement.total_users}</span>
              </p>
              <div className="mt-2 h-1.5 bg-novatus rounded-full overflow-hidden">
                <div className="h-full bg-novatus-green rounded-full transition-all" style={{ width: `${engagement.onboarding.completion_rate}%` }} />
              </div>
              <p className="text-xs text-novatus-text-3 mt-2">
                <span className="text-novatus-green font-semibold">{engagement.onboarding.completion_rate}%</span> abgeschlossen
                {engagement.onboarding.pending > 0 && ` · ${engagement.onboarding.pending} offen`}
              </p>
            </div>

            {/* Active 7d */}
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-4" data-testid="engagement-active-7d">
              <p className="label-eyebrow">Aktiv (7 Tage)</p>
              <p className="font-display font-black text-3xl text-white mt-1">
                {engagement.login_activity.active_7d}<span className="text-novatus-text-3 text-xl">/{engagement.total_users}</span>
              </p>
              <p className="text-xs text-novatus-text-3 mt-3">Logins der letzten 7 Tage</p>
            </div>

            {/* Active 30d */}
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-4" data-testid="engagement-active-30d">
              <p className="label-eyebrow">Aktiv (30 Tage)</p>
              <p className="font-display font-black text-3xl text-white mt-1">
                {engagement.login_activity.active_30d}<span className="text-novatus-text-3 text-xl">/{engagement.total_users}</span>
              </p>
              <p className="text-xs text-novatus-text-3 mt-3">Logins der letzten 30 Tage</p>
            </div>

            {/* Trainings 30d */}
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-4" data-testid="engagement-trainings-30d">
              <p className="label-eyebrow">Trainings (30 Tage)</p>
              <p className="font-display font-black text-3xl text-novatus-green mt-1">
                {engagement.training_activity.sessions_last_30d}
              </p>
              <p className="text-xs text-novatus-text-3 mt-3">
                Ø {engagement.training_activity.sessions_per_user_30d} pro Nutzer · {engagement.training_activity.sessions_last_7d} in 7 Tagen
              </p>
            </div>
          </div>

          {/* Stale accounts (>90 days) */}
          {engagement.stale_accounts.count > 0 && (
            <div className="mt-5 pt-5 border-t border-novatus">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Timer className="w-4 h-4 text-amber-400" />
                  <h4 className="font-display font-bold text-white text-sm">
                    Veraltete Accounts <span className="text-novatus-text-3 font-normal">(&gt; {engagement.stale_accounts.threshold_days} Tage inaktiv)</span>
                  </h4>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300">
                    {engagement.stale_accounts.count}
                  </span>
                </div>
                <Link to="/admin" onClick={() => { try { sessionStorage.setItem("admin_open_tab", "users"); } catch (e) { /* noop */ } }} className="text-xs text-novatus-green hover:underline">
                  Im Admin verwalten →
                </Link>
              </div>
              <p className="text-xs text-novatus-text-3 mb-3">
                Diese Accounts sind länger als 90 Tage inaktiv und sind im Admin-Bereich mit dem Badge „Veraltet" markiert. Lösche sie dort manuell, wenn nicht mehr benötigt.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {engagement.stale_accounts.users.slice(0, 8).map(u => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-novatus-card-2 border border-amber-500/20 text-sm"
                    data-testid={`stale-account-${u.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white truncate">{u.first_name} {u.last_name}</span>
                        {u.is_demo_account && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-blue-400/15 border border-blue-400/40 text-blue-300 shrink-0">Demo</span>
                        )}
                      </div>
                      <p className="text-xs text-novatus-text-3 truncate font-mono">{u.email}</p>
                    </div>
                    <span className="text-xs font-mono text-amber-300 shrink-0 whitespace-nowrap">
                      {u.days_inactive != null ? `${u.days_inactive} Tage` : "—"}
                    </span>
                  </div>
                ))}
              </div>
              {engagement.stale_accounts.users.length > 8 && (
                <p className="text-xs text-novatus-text-3 mt-2 text-center">
                  + {engagement.stale_accounts.users.length - 8} weitere
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Weak categories + Top 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <div className="lg:col-span-2 nv-card p-6 animate-slide-up delay-3" data-testid="weak-categories-card">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <h3 className="font-display font-bold text-lg text-white">Schwache Kategorien (Team)</h3>
          </div>
          {data.team_weakest_categories?.length === 0 ? (
            <p className="text-sm text-novatus-text-3">Noch zu wenige Auswertungen vorhanden.</p>
          ) : (
            <div className="space-y-3">
              {data.team_weakest_categories.map((c, i) => (
                <div key={c.name} className="flex items-center gap-4">
                  <span className="font-display font-black text-2xl text-amber-400 w-10">0{i+1}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-white capitalize">{c.name.replace(/_/g, " ")}</p>
                    <div className="mt-1 h-2 bg-novatus-card-2 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${c.score}%` }} />
                    </div>
                  </div>
                  <span className="font-display font-black text-xl text-white">{c.score}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-5 pt-4 border-t border-novatus">
            <p className="label-eyebrow mb-3">Alle Kategorien</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(data.team_category_averages || {}).map(([k, v]) => (
                <div key={k} className="bg-novatus-card-2 border border-novatus rounded-lg p-3">
                  <div className="font-display font-black text-lg text-white">{v}</div>
                  <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-0.5 leading-tight capitalize">{k.replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="nv-card p-6 animate-slide-up delay-3" data-testid="top-performers-card">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-novatus-green" />
            <h3 className="font-display font-bold text-lg text-white">Top 3 Performer</h3>
          </div>
          <div className="space-y-3">
            {top3.length === 0 ? <p className="text-sm text-novatus-text-3">Noch keine Daten.</p> :
              top3.map((u, i) => (
                <div key={u.id} className="flex items-center gap-3" data-testid={`top-performer-${i}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-sm ${
                    i === 0 ? "bg-novatus-green text-black" : i === 1 ? "bg-novatus-card-2 border border-novatus-green/40 text-novatus-green" : "bg-novatus-card-2 border border-novatus text-novatus-text-2"
                  }`}>{i + 1}</div>
                  <div className="flex-1">
                    <p className="font-semibold text-white text-sm">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-novatus-text-3">{u.level} · {u.total_sessions} Trainings</p>
                  </div>
                  <span className="font-display font-black text-2xl text-novatus-green">{u.average_score}</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Per-user table */}
      <div className="nv-card mt-5 overflow-x-auto animate-slide-up delay-4">
        <div className="p-4 border-b border-novatus flex items-center gap-2">
          <Users2 className="w-4 h-4 text-novatus-green" />
          <h3 className="font-display font-bold text-white">Verkäufer · Performance & Schwächen</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-novatus-card-2 border-b border-novatus">
            <tr className="text-left">
              <th className="px-4 py-3 label-eyebrow">Verkäufer</th>
              <th className="px-4 py-3 label-eyebrow">Level</th>
              <th className="px-4 py-3 label-eyebrow text-right">Trainings</th>
              <th className="px-4 py-3 label-eyebrow text-right">Ø Score</th>
              <th className="px-4 py-3 label-eyebrow">Schwächen (Top 2)</th>
              <th className="px-4 py-3 label-eyebrow text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(u => (
              <tr key={u.id} className="border-b border-novatus hover:bg-white/[0.02]" data-testid={`team-user-${u.id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-white">{u.first_name} {u.last_name}</p>
                  <p className="text-xs text-novatus-text-3">{u.email}</p>
                </td>
                <td className="px-4 py-3 text-novatus-text-2">{u.level}</td>
                <td className="px-4 py-3 text-right font-mono text-white">{u.total_sessions}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`font-display font-black text-xl ${u.average_score >= 70 ? "text-novatus-green" : u.average_score >= 50 ? "text-amber-400" : "text-red-400"}`}>{u.average_score}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {u.weakest_categories?.length === 0 ? <span className="text-xs text-novatus-text-3">—</span> :
                      u.weakest_categories?.map(c => (
                        <span key={c} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-300 border border-amber-400/30 capitalize">{c.replace(/_/g, " ")}</span>
                      ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/team/users/${u.id}/profile`} className="text-xs btn-ghost-green h-8 px-3 rounded-md inline-flex items-center gap-1" data-testid={`profile-user-${u.id}`}>
                      <ChevronRight className="w-3 h-3" /> Profil
                    </Link>
                    <button onClick={() => { setAssignTarget({ ...assignTarget, user_id: u.id }); setShowAssign(true); }} className="text-xs btn-ghost-green h-8 px-3 rounded-md inline-flex items-center gap-1" data-testid={`assign-user-${u.id}`}>
                      <Clipboard className="w-3 h-3" /> Zuweisen
                    </button>
                    <button
                      onClick={() => setConfirmDelete({ id: u.id, name: `${u.first_name} ${u.last_name}`, email: u.email })}
                      className="text-xs h-8 w-8 rounded-md inline-flex items-center justify-center text-novatus-text-3 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                      data-testid={`delete-user-${u.id}`}
                      aria-label={`${u.first_name} ${u.last_name} löschen`}
                      title="Benutzer löschen"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assignments + Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <div className="nv-card animate-slide-up">
          <div className="p-4 border-b border-novatus flex items-center gap-2">
            <Clipboard className="w-4 h-4 text-novatus-green" />
            <h3 className="font-display font-bold text-white">Pflicht-Zuweisungen ({assignments.length})</h3>
          </div>
          <div className="divide-y divide-novatus max-h-96 overflow-y-auto">
            {assignments.length === 0 ? <p className="p-4 text-sm text-novatus-text-3">Noch keine Zuweisungen.</p> :
              assignments.map(a => (
                <div key={a.id} className="p-4 flex items-center justify-between gap-3" data-testid={`assignment-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm truncate">{a.scenario_title}</p>
                    <p className="text-xs text-novatus-text-3">an {a.user_name} · {new Date(a.created_at).toLocaleDateString("de-DE")}</p>
                    {a.note && <p className="text-xs text-novatus-text-2 mt-1 italic">"{a.note}"</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {a.status === "completed"
                      ? <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40">erledigt {a.score ? `· ${a.score}` : ""}</span>
                      : <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-300 border border-amber-400/30">offen</span>}
                    <button onClick={() => deleteAssignment(a.id)} className="p-1.5 hover:bg-red-500/10 rounded-md">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="nv-card animate-slide-up">
          <div className="p-4 border-b border-novatus flex items-center gap-2">
            <Activity className="w-4 h-4 text-novatus-green" />
            <h3 className="font-display font-bold text-white">Letzte Aktivität</h3>
          </div>
          <div className="divide-y divide-novatus max-h-96 overflow-y-auto">
            {data.recent_activity?.length === 0 ? <p className="p-4 text-sm text-novatus-text-3">Keine Aktivität.</p> :
              data.recent_activity?.map((r, i) => (
                <Link
                  to={`/team/sessions/${r.id}`}
                  key={r.id || i}
                  className="p-4 flex items-center justify-between gap-3 hover:bg-white/[0.02] transition-colors"
                  data-testid={`recent-activity-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm">{r.user_name}</p>
                    <p className="text-xs text-novatus-text-3 truncate">{r.scenario_title} · {new Date(r.ended_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-novatus-green">+{r.xp_earned} XP</span>
                    <span className={`font-display font-black text-lg ${r.score >= 70 ? "text-novatus-green" : r.score >= 50 ? "text-amber-400" : "text-red-400"}`}>{r.score}</span>
                    <ChevronRight className="w-4 h-4 text-novatus-text-3" />
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShowAssign(false)}>
          <div className="nv-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="assignment-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl text-white">Pflicht-Training zuweisen</h3>
              <button onClick={() => setShowAssign(false)} className="p-1.5 hover:bg-white/5 rounded-md"><X className="w-4 h-4 text-novatus-text-2" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-eyebrow block mb-1.5">Verkäufer</label>
                <select value={assignTarget.user_id} onChange={e => setAssignTarget({...assignTarget, user_id: e.target.value})} className="nv-input w-full" data-testid="assign-user-select">
                  <option value="">— wählen —</option>
                  {data.users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Szenario</label>
                <select value={assignTarget.scenario_id} onChange={e => setAssignTarget({...assignTarget, scenario_id: e.target.value})} className="nv-input w-full" data-testid="assign-scenario-select">
                  <option value="">— wählen —</option>
                  {scenarios.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Notiz (optional)</label>
                <textarea rows={2} value={assignTarget.note} onChange={e => setAssignTarget({...assignTarget, note: e.target.value})} className="nv-input w-full" placeholder="z. B.: Bitte mit Fokus auf JAZ-Argument..." data-testid="assign-note" />
              </div>
              <button onClick={createAssignment} className="btn-green w-full h-11 rounded-lg flex items-center justify-center gap-2" data-testid="assign-submit-btn">
                <Clipboard className="w-4 h-4" /> Zuweisen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete-User Confirm Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setConfirmDelete(null)}>
          <div className="nv-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-user-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-400/10 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-display font-bold text-xl text-white">Benutzer löschen?</h3>
                <p className="text-sm text-novatus-text-2 mt-1">
                  <span className="text-white font-semibold">{confirmDelete.name}</span> <span className="text-novatus-text-3">({confirmDelete.email})</span> wird inklusive aller Sessions, Nachrichten, Zuweisungen und Feedbacks unwiderruflich gelöscht.
                </p>
              </div>
            </div>
            <div className="rounded-lg bg-red-400/5 border border-red-400/30 px-3 py-2.5 text-xs text-novatus-text-2 mb-5">
              <strong className="text-red-400">DSGVO-Hinweis:</strong> Diese Aktion ist nicht rückgängig zu machen. Bei Arbeitnehmer-Konten beachte deine Aufbewahrungs-Pflichten.
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-lg text-sm text-novatus-text-2 hover:text-white hover:bg-white/5" data-testid="delete-cancel-btn">
                Abbrechen
              </button>
              <button onClick={deleteUser} className="px-4 py-2 rounded-lg text-sm bg-red-500 hover:bg-red-600 text-white font-semibold inline-flex items-center gap-2" data-testid="delete-confirm-btn">
                <Trash2 className="w-3.5 h-3.5" /> Endgültig löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create-Org Modal */}
      {showCreateOrg && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCreateOrg(false)} data-testid="create-org-modal">
          <div className="nv-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-white text-lg">Organisation anlegen</h3>
              <button onClick={() => setShowCreateOrg(false)} className="p-1.5 hover:bg-white/5 rounded-md">
                <X className="w-5 h-5 text-novatus-text-3" />
              </button>
            </div>
            <label className="label-eyebrow">Name der Organisation</label>
            <input
              type="text"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="z.B. Novatus Vertrieb Nord"
              className="w-full mt-1 bg-novatus-card-2 border border-novatus rounded-lg px-3 py-2 text-white placeholder-novatus-text-3 focus:outline-none focus:border-novatus-green"
              data-testid="new-org-name-input"
            />
            <p className="text-xs text-novatus-text-3 mt-2 leading-relaxed">
              Du wirst zum Owner. Team/Enterprise-Abos werden danach automatisch dem Pool gutgeschrieben.
            </p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowCreateOrg(false)} className="flex-1 h-10 rounded-lg border border-novatus text-novatus-text-2 hover:bg-white/5">
                Abbrechen
              </button>
              <button onClick={createOrg} disabled={orgBusy || !newOrgName.trim()} className="btn-green flex-1 h-10 rounded-lg disabled:opacity-50" data-testid="create-org-submit">
                {orgBusy ? "Lege an..." : "Anlegen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDashboard;
